import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * Minimum quantity allowed
 */
const MIN_QUANTITY = 5;

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    const { minusButton, plusButton, quantityInput } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }

    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.initialized = true;

    // Force minimum quantity to 5
    const currentValue = parseIntOrDefault(quantityInput.value, MIN_QUANTITY);

    if (currentValue < MIN_QUANTITY) {
      quantityInput.value = MIN_QUANTITY.toString();
    }

    quantityInput.min = MIN_QUANTITY.toString();

    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute(
      'data-cart-quantity',
      cartQty.toString()
    );

    this.updateCartQuantity();
  }

  /**
   * Checks if current quantity can be added to cart
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();

    const quantityToAdd = value;
    const wouldExceedMax =
      max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  getValue() {
    return this.refs.quantityInput.value;
  }

  setValue(value) {
    const numericValue = parseInt(value, 10) || MIN_QUANTITY;

    this.refs.quantityInput.value = Math.max(
      MIN_QUANTITY,
      numericValue
    ).toString();

    this.updateButtonStates();
  }

  /**
   * Updates min/max/step constraints
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;

    const currentValue =
      parseInt(quantityInput.value, 10) || MIN_QUANTITY;

    // Minimum is always 5
    const newMin = Math.max(
      MIN_QUANTITY,
      parseIntOrDefault(min, MIN_QUANTITY)
    );

    const newStep = parseIntOrDefault(step, 1);

    // Always use minimum 5 in HTML input
    quantityInput.min = newMin.toString();

    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }

    // Keep normal increment of 1
    quantityInput.step = newStep.toString();

    const effectiveMax = this.getEffectiveMax();

    let newValue = currentValue;

    // Never allow quantity below 5
    if (newValue < newMin) {
      newValue = newMin;
    }

    // Snap to valid increment
    if ((newValue - newMin) % newStep !== 0) {
      newValue =
        newMin +
        Math.floor((newValue - newMin) / newStep) * newStep;
    }

    // Make sure value stays within limits
    newValue = Math.max(
      newMin,
      Math.min(effectiveMax ?? Infinity, newValue)
    );

    quantityInput.value = newValue.toString();

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      // Minimum is ALWAYS 5
      min: Math.max(
        MIN_QUANTITY,
        parseIntOrDefault(quantityInput.min, MIN_QUANTITY)
      ),

      max: parseIntOrDefault(quantityInput.max, null),

      step: parseIntOrDefault(quantityInput.step, 1),

      value: Math.max(
        MIN_QUANTITY,
        parseIntOrDefault(quantityInput.value, MIN_QUANTITY)
      ),

      cartQuantity: parseIntOrDefault(
        quantityInput.getAttribute('data-cart-quantity'),
        0
      ),
    };
  }

  /**
   * Gets effective maximum
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();

    if (max === null) return null;

    return Math.max(max - cartQuantity, min);
  }

  /**
   * Updates button states
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled =
        effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Updates quantity
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;

    const { min, step, value } = this.getCurrentValues();

    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(
        min,
        value + step * stepMultiplier
      )
    );

    quantityInput.value = newValue.toString();

    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Increase quantity
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    // +1
    this.updateQuantity(1);
  }

  /**
   * Decrease quantity
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    // -1 but never below 5
    this.updateQuantity(-1);
  }

  /**
   * Select input value
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;

    if (
      !(event.target instanceof HTMLInputElement) ||
      document.activeElement !== quantityInput
    ) {
      return;
    }

    quantityInput.setAttribute(
      'data-skip-value-update',
      'true'
    );

    quantityInput.select();
  }

  /**
   * Handles manually entered quantity
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();

    const { quantityInput } = this.refs;

    quantityInput.removeAttribute(
      'data-skip-value-update'
    );

    const { min, step } = this.getCurrentValues();

    const effectiveMax = this.getEffectiveMax();

    let quantity =
      parseInt(event.target.value, 10) || MIN_QUANTITY;

    // Minimum = 5
    quantity = Math.max(min, quantity);

    // Maximum
    quantity = Math.min(
      effectiveMax ?? Infinity,
      quantity
    );

    // Correct invalid step
    if ((quantity - min) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();

    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Quantity changed
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;

    const newValue = Math.max(
      MIN_QUANTITY,
      parseInt(quantityInput.value, 10) || MIN_QUANTITY
    );

    quantityInput.value = newValue.toString();

    this.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        Number(quantityInput.dataset.cartLine) || undefined
      )
    );
  }

  /**
   * Updates cart quantity
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;

    const { min, value } = this.getCurrentValues();

    const effectiveMax = this.getEffectiveMax();

    const clampedValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(min, value)
    );

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />'
      );
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define(
    'quantity-selector-component',
    QuantitySelectorComponent
  );
}